/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { ORDERED_PARAMETER_TABS } from "constants/tabIds"

import { tabFieldMapping } from "features/userParameters/UserParametersForm"
import { CurrentUserParameters } from "features/userParameters/UserParametersSchemata"
import { FieldErrors } from "react-hook-form"

/**
 * Count errors and map them to their corresponding tabs
 * @param errors Form errors object
 * @returns Record mapping tab IDs to error counts
 */
export const createTabErrorMap = (
  errors: FieldErrors<CurrentUserParameters>
): Record<string, number> => {
  const errorMap: Record<string, number> = {}

  // Recursive helper to traverse the error object and accumulate error counts
  const countErrors = (obj: unknown, path = ""): void => {
    if (!obj) return

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        countErrors(item, `${path}[${index}]`)
      })
      return
    }

    if (typeof obj === "object") {
      // If the object has a message property, consider it an error object
      if ("message" in obj && typeof obj.message === "string") {
        for (const [tabId, fields] of Object.entries(tabFieldMapping)) {
          if (fields.some(field => path.startsWith(field))) {
            errorMap[tabId] = (errorMap[tabId] || 0) + 1
            break
          }
        }
        return
      }
      // Otherwise, iterate over its properties
      Object.entries(obj).forEach(([key, value]: readonly [string, unknown]) => {
        const newPath = path ? `${path}.${key}` : key
        countErrors(value, newPath)
      })
    }
  }

  countErrors(errors)
  return errorMap
}

/**
 * Find the first tab with errors based on natural tab order
 * @param errorMap Record mapping tab IDs to error counts
 * @returns The first tab ID with errors or undefined if none found
 */
export const findFirstErrorTab = (
  errorMap: Readonly<Record<string, number>>
): string | undefined => {
  const tabsWithErrors = Object.keys(errorMap)
  if (tabsWithErrors.length === 0) return undefined

  return ORDERED_PARAMETER_TABS.find(tab => tabsWithErrors.includes(tab))
}

/**
 * Set the active tab to the first tab with errors
 * @param errors Form errors object
 * @param setActiveTab Function to set the active tab
 * @returns The error map created during processing
 */
export const navigateToFirstErrorTab = (
  errors: FieldErrors<CurrentUserParameters>,
  setActiveTab: (tab: string) => void
): Record<string, number> => {
  const errorMap = createTabErrorMap(errors)
  const firstErrorTab = findFirstErrorTab(errorMap)

  if (firstErrorTab) {
    setActiveTab(firstErrorTab)
  }

  return errorMap
}

/**
 * Count the total number of errors in the form
 * @param errorMap Record mapping tab IDs to error counts
 * @returns Total number of errors
 */
export const countTotalErrors = (errorMap: Readonly<Record<string, number>>): number => {
  return Object.values(errorMap).reduce((sum, count) => sum + count, 0)
}
